import { FhirBundleSdk } from "@metriport/fhir-sdk";
import { useMemo, useState } from "react";
import type { ColumnDef } from "../../components/base-table-layout";
import { createEhrWriteBackColumn } from "../../ehr/create-ehr-column";
import { useEhrContext } from "../../../shared-logic/ehr-context/ehr-context";
import { BaseTableLayout } from "../../components/base-table-layout";
import { TableRow } from "../../utils/tables-metadata";
import { VitalRowData } from "./vital-rows";
import { UnitSystem, UnitSystemToggle } from "./unit-system-toggle";

export type VitalTableProps = {
  rows: TableRow[];
  onRowClick: (row: TableRow) => void;
  fhirSdk: FhirBundleSdk;
  filterQuery: string;
  onPdfView?: (docId: string) => void;
  displayName?: string;
  isRowViewed?: (row: TableRow) => boolean;
};

export function VitalTable({
  rows,
  onRowClick,
  filterQuery,
  displayName = "Vitals",
  isRowViewed,
}: VitalTableProps) {
  const [unitSystem, setUnitSystem] = useState<UnitSystem>("metric");
  const ehrContext = useEhrContext();

  const columns = useMemo(() => {
    const cols: ColumnDef<VitalRowData>[] = [
      { key: "observation", header: "Observation" },
      {
        key: "mostRecentValue",
        header: "Most Recent Value",
        render: row => row.displayValue(unitSystem),
      },
      { key: "mostRecentDate", header: "Most Recent Date", maxWidth: "150px", sort: "desc" },
    ];

    const ehrColumn = createEhrWriteBackColumn<VitalRowData>(ehrContext, "vitals");
    if (ehrColumn) cols.push(ehrColumn);

    return cols;
  }, [ehrContext, unitSystem]);

  function handleRowClick(row: TableRow) {
    onRowClick(row);
  }

  return (
    <BaseTableLayout
      title={displayName}
      rows={rows as unknown as VitalRowData[]}
      columns={columns}
      filterQuery={filterQuery}
      onRowClick={handleRowClick}
      isRowViewed={isRowViewed}
      headerActionsSlot={<UnitSystemToggle unitSystem={unitSystem} onChange={setUnitSystem} />}
    />
  );
}
